/**
 * utils/sorProcessorUtils.js
 * Fonctions utilitaires partagées par les processeurs SOR
 */

import logger from "./logger.js";

export class SorProcessorUtils {
  /**
   * Trier les fibres par tiroir → module → port
   * @param {Array} fibers - Liste des fibres à trier
   * @returns {Array} Fibres triées
   */
  static sortFibers(fibers) {
    return fibers.sort((a, b) => {
      // Tri par tiroir (extraction du nombre: T12 -> 12)
      const ta = +String(a.tiroir).replace(/^\D+/, "");
      const tb = +String(b.tiroir).replace(/^\D+/, "");
      if (ta !== tb) return ta - tb;

      // Tri par module (ordre alphabétique)
      if (a.module !== b.module) {
        return String(a.module).localeCompare(String(b.module));
      }

      // Tri par port (ordre numérique)
      return +a.port - +b.port;
    });
  }

  /**
   * Construire la référence complète d'une fibre
   * @param {string} tiroir - Identifiant du tiroir (ex: "T12")
   * @param {string} moduleCode - Code du module (ex: "A")
   * @returns {string} Référence formatée (ex: "B2-T12-MA")
   */
  static buildReference(tiroir, moduleCode) {
    return `B2-${tiroir}-M${moduleCode}`;
  }

  /**
   * Extraire le code module depuis le nom complet
   * @param {string} module - Nom du module (ex: "MODULE_A")
   * @returns {string} Code du module (ex: "A")
   */
  static extractModuleCode(module) {
    return module.replace("MODULE_", "");
  }

  /**
   * Logger une erreur de traitement de fibre de manière standardisée
   * @param {Error} error - L'erreur capturée
   * @param {string} processorType - Type de processeur (ex: "pvSuccess", "pvEchec")
   * @param {number} fiberIndex - Index de la fibre dans le groupe
   * @param {string} tiroir - Identifiant du tiroir
   * @param {string} module - Identifiant du module
   * @param {Object} fiberData - Données de contexte de la fibre
   */
  static logFiberError(error, processorType, fiberIndex, tiroir, module, fiberData) {
    logger.error(
      {
        context: "fiber-processing-error",
        processorType,
        fiberIndex,
        tiroir,
        module,
        error: error.message,
        stack: error.stack,
        fiberData,
      },
      `Erreur sur fiber ${fiberIndex} dans ${tiroir}-${module}`
    );
  }

  /**
   * Valider la structure de base d'une fibre
   * @param {Object} fiber - Objet fibre à valider
   * @param {Array<string>} requiredFields - Champs requis
   * @throws {Error} Si un champ requis est manquant
   */
  static validateFiberStructure(fiber, requiredFields) {
    for (const field of requiredFields) {
      if (!fiber[field]) {
        throw new Error(`fiber.${field} est undefined/null`);
      }
    }
  }

  /**
   * Extraire de manière sécurisée le premier élément d'un Set
   * @param {Set} set - Set dont extraire le premier élément
   * @returns {*} Premier élément ou null
   */
  static getFirstFromSet(set) {
    if (!set || set.size === 0) return null;
    return Array.from(set)[0];
  }
}
